"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

export default function TechStackAccordionForMobile() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDark } = useTheme();

  // ✅ SEO: tech stack items are crawlable text — not hidden behind JS-only rendering
  const techStack = {
    frontend: { title: "Mobile Frontend",   color: "blue",   items: ["Flutter", "React Native", "Swift (iOS)", "Kotlin (Android)"] },
    backend:  { title: "Backend & APIs",    color: "green",  items: ["Node.js", "Express.js", "Firebase", "Django", "GraphQL"] },
    database: { title: "Databases",         color: "purple", items: ["MongoDB", "SQLite", "Firebase Firestore", "MySQL"] },
    tools:    { title: "Tools & Platforms", color: "orange", items: ["Android Studio", "Xcode", "VS Code", "Postman", "Figma"] },
  };

  const colorClasses = {
    blue:   "bg-blue-500",
    green:  "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    show:   { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  const wrapperBg = isDark ? "bg-zinc-900"   : "bg-[#1a2e1a]";
  const cardBg    = isDark ? "bg-zinc-800"   : "bg-[#0f1f0f]";
  const subText   = isDark ? "text-gray-400" : "text-gray-300";

  return (
    // ✅ SEO: <section> landmark with aria-label so Google indexes this as "Tech Stack" content
    <section
      aria-labelledby="tech-stack-heading"
      className="w-full max-w-6xl mx-auto p-4 -mt-8 md:-mt-12"
    >
      {/* ✅ SEO: visually hidden H2 so heading hierarchy is maintained and Google sees the keyword */}
      <h2
        id="tech-stack-heading"
        className="sr-only"
      >
        Mobile App Development Tech Stack — Flutter, React Native, Firebase, Node.js
      </h2>

      {/* Accordion trigger */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="tech-stack-content"
        className={`w-full ${wrapperBg} border-2 border-green-400 rounded-2xl p-6 flex items-center justify-between group transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.6)]`}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0"
            aria-hidden="true"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M9 10l-4.553-2.276A1 1 0 003 8.618v6.764a1 1 0 001.447.894L9 14m6 0v2a2 2 0 11-4 0v-2m4 0a4 4 0 10-8 0" />
            </svg>
          </div>
          <div className="text-left">
            {/* ✅ SEO: descriptive button label includes keyword "tech stack" */}
            <p className="text-white text-xl md:text-2xl font-semibold">For the technically curious</p>
            <p className={`${subText} text-sm md:text-base mt-1`}>
              Expand to see our full mobile app tech stack
            </p>
          </div>
        </div>
        <div className="flex-shrink-0" aria-hidden="true">
          <svg
            className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* ✅ SEO: id matches aria-controls on button — screen readers + crawlers can associate them */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id="tech-stack-content"
            role="region"
            aria-labelledby="tech-stack-heading"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="mt-4 overflow-hidden"
          >
            <motion.div
              className={`${wrapperBg} rounded-2xl p-6 md:p-8`}
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              {/* ✅ SEO: <dl> (description list) is semantically correct for tech category → items pairs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {Object.entries(techStack).map(([key, category]) => (
                  <motion.div
                    key={key}
                    variants={itemVariants}
                    className={`space-y-4 ${cardBg} p-4 rounded-xl hover:shadow-lg hover:shadow-green-400 transition-all`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${colorClasses[category.color]}`}
                        aria-hidden="true"
                      />
                      {/* ✅ SEO: h3 under the sr-only h2 — correct heading hierarchy */}
                      <h3 className="text-white font-semibold text-lg">{category.title}</h3>
                    </div>
                    {/* ✅ SEO: tech items in a <ul> list — crawlable structured content */}
                    <ul className="flex flex-wrap gap-x-2 gap-y-1" aria-label={`${category.title} technologies`}>
                      {category.items.map((item, i) => (
                        <li key={item} className="text-gray-300 text-sm">
                          {item}{i < category.items.length - 1 ? "," : ""}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ SEO: hidden paragraph listing all tech keywords — always in DOM for crawlers even when accordion is closed */}
      <p className="sr-only">
        Our mobile app development tech stack includes: Flutter, React Native, Swift, Kotlin for mobile frontend;
        Node.js, Express.js, Firebase, Django, GraphQL for backend and APIs;
        MongoDB, SQLite, Firebase Firestore, MySQL for databases;
        Android Studio, Xcode, VS Code, Postman, and Figma for tools and platforms.
      </p>
    </section>
  );
}